import { useEffect, useRef, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Hammer, Users, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function LojistaObras() {
  const [obras, setObras] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [modalObra, setModalObra] = useState(false);
  const [modalPrestador, setModalPrestador] = useState(false);
  const [obraForm, setObraForm] = useState({ titulo: "", descricao: "", tipo: "INTERNA", data_inicio: "", data_fim: "" });
  const [prestForm, setPrestForm] = useState({ nome: "", rg: "", cpf: "", funcao: "" });
  const [uploadObraId, setUploadObraId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [o, p] = await Promise.all([api.get("/obras/lojista"), api.get("/obras/prestadores/lojista")]);
    setObras(o.data);
    setPrestadores(p.data);
  };
  useEffect(() => { load(); }, []);

  const criarObra = async () => {
    try {
      const payload = {
        ...obraForm,
        data_inicio: new Date(obraForm.data_inicio).toISOString(),
        data_fim: new Date(obraForm.data_fim).toISOString(),
      };
      await api.post("/obras/lojista", payload);
      toast.success("Solicitação enviada — aguardando aprovação");
      setModalObra(false);
      setObraForm({ titulo: "", descricao: "", tipo: "INTERNA", data_inicio: "", data_fim: "" });
      load();
    } catch { toast.error("Falha ao criar obra"); }
  };

  const criarPrestador = async () => {
    try {
      await api.post("/obras/prestadores/lojista", prestForm);
      toast.success("Prestador cadastrado — autorizado na portaria");
      setModalPrestador(false);
      setPrestForm({ nome: "", rg: "", cpf: "", funcao: "" });
      load();
    } catch { toast.error("Falha ao cadastrar"); }
  };

  const delPrestador = async (id) => {
    await api.delete(`/obras/prestadores/lojista/${id}`);
    toast.success("Removido");
    load();
  };

  const uploadAnexo = async (obraId) => {
    if (!fileRef.current?.files?.[0]) return;
    const fd = new FormData();
    fd.append("file", fileRef.current.files[0]);
    await api.post(`/obras/${obraId}/anexo-lojista`, fd);
    toast.success("Anexo enviado");
    setUploadObraId(null);
    load();
  };

  const statusBadge = (s) => {
    const m = {
      AGUARDANDO_APROVACAO: { c: "border-warning/40 text-warning bg-warning/5", l: "Aguardando" },
      APROVADA: { c: "border-success/40 text-success bg-success/5", l: "Aprovada" },
      REJEITADA: { c: "border-destructive/40 text-destructive bg-destructive/5", l: "Rejeitada" },
    };
    const x = m[s] || { c: "border-border", l: s };
    return <Badge variant="outline" className={`${x.c} text-[10px] uppercase tracking-widest`}>{x.l}</Badge>;
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <div className="label-tag mb-2">Reformas · Manutenções · Prestadores</div>
        <h1 className="font-heading font-black tracking-tighter text-3xl">Obras</h1>
      </div>

      <Tabs defaultValue="obras">
        <TabsList className="w-full rounded-xl">
          <TabsTrigger value="obras" data-testid="loj-tab-obras" className="flex-1 rounded-xl"><Hammer size={12} className="mr-2" /> Obras</TabsTrigger>
          <TabsTrigger value="prestadores" data-testid="loj-tab-prest" className="flex-1 rounded-xl"><Users size={12} className="mr-2" /> Prestadores</TabsTrigger>
        </TabsList>

        <TabsContent value="obras" className="mt-4 space-y-3">
          <Dialog open={modalObra} onOpenChange={setModalObra}>
            <DialogTrigger asChild>
              <Button data-testid="loj-nova-obra" className="w-full rounded-xl bg-foreground text-background hover:bg-foreground/90 h-12">
                <Plus size={14} className="mr-2" /> Solicitar nova obra
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Solicitar obra</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-tag">Título</Label>
                  <Input data-testid="obra-titulo" value={obraForm.titulo} onChange={(e) => setObraForm({ ...obraForm, titulo: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label className="label-tag">Tipo</Label>
                  <Select value={obraForm.tipo} onValueChange={(v) => setObraForm({ ...obraForm, tipo: v })}>
                    <SelectTrigger data-testid="obra-tipo" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INTERNA">Interna (dentro da loja)</SelectItem>
                      <SelectItem value="FACHADA">Fachada</SelectItem>
                      <SelectItem value="MANUTENCAO">Manutenção</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-tag">Descrição</Label>
                  <Textarea data-testid="obra-desc" value={obraForm.descricao} onChange={(e) => setObraForm({ ...obraForm, descricao: e.target.value })} className="rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">Início</Label>
                    <Input data-testid="obra-inicio" type="date" value={obraForm.data_inicio} onChange={(e) => setObraForm({ ...obraForm, data_inicio: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="label-tag">Fim</Label>
                    <Input data-testid="obra-fim" type="date" value={obraForm.data_fim} onChange={(e) => setObraForm({ ...obraForm, data_fim: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModalObra(false)} className="rounded-xl">Cancelar</Button>
                <Button data-testid="obra-salvar" onClick={criarObra} className="rounded-xl bg-foreground text-background hover:bg-foreground/90">Enviar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {obras.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-sm border border-dashed rounded-2xl">
              Nenhuma obra solicitada ainda.
            </div>
          ) : (
            obras.map((o) => (
              <Card key={o.id} data-testid={`loj-obra-${o.id}`} className="p-5 rounded-2xl border-border">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold">{o.titulo}</span>
                      {statusBadge(o.status)}
                    </div>
                    <Badge variant="outline" className="text-[10px] mt-1">{o.tipo}</Badge>
                    <p className="text-sm mt-2 text-muted-foreground leading-relaxed">{o.descricao}</p>
                    <div className="text-xs font-mono text-muted-foreground mt-2">
                      {fmtDate(o.data_inicio)} → {fmtDate(o.data_fim)}
                    </div>
                    {o.anexos?.length > 0 && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {o.anexos.length} anexo(s)
                      </div>
                    )}
                    {o.observacoes_admin && (
                      <div className="text-xs mt-2 border-l-2 border-border pl-3 text-muted-foreground">
                        <b>Retorno:</b> {o.observacoes_admin}
                      </div>
                    )}
                  </div>
                </div>
                {o.status === "AGUARDANDO_APROVACAO" && (
                  <div className="mt-3 pt-3 border-t border-border/60">
                    {uploadObraId === o.id ? (
                      <div className="flex gap-2">
                        <Input type="file" ref={fileRef} className="rounded-xl text-xs" data-testid={`upload-file-${o.id}`} />
                        <Button size="sm" onClick={() => uploadAnexo(o.id)} data-testid={`upload-enviar-${o.id}`} className="rounded-xl">
                          Enviar
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setUploadObraId(o.id)} className="rounded-xl w-full" data-testid={`upload-abrir-${o.id}`}>
                        <Upload size={12} className="mr-2" /> Anexar projeto/foto
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="prestadores" className="mt-4 space-y-3">
          <Dialog open={modalPrestador} onOpenChange={setModalPrestador}>
            <DialogTrigger asChild>
              <Button data-testid="loj-novo-prestador" className="w-full rounded-xl bg-foreground text-background hover:bg-foreground/90 h-12">
                <Plus size={14} className="mr-2" /> Cadastrar prestador
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Novo prestador</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-tag">Nome completo</Label>
                  <Input data-testid="prest-nome" value={prestForm.nome} onChange={(e) => setPrestForm({ ...prestForm, nome: e.target.value })} className="rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">RG</Label>
                    <Input data-testid="prest-rg" value={prestForm.rg} onChange={(e) => setPrestForm({ ...prestForm, rg: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="label-tag">CPF (opcional)</Label>
                    <Input data-testid="prest-cpf" value={prestForm.cpf} onChange={(e) => setPrestForm({ ...prestForm, cpf: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label className="label-tag">Função</Label>
                  <Input data-testid="prest-funcao" value={prestForm.funcao} onChange={(e) => setPrestForm({ ...prestForm, funcao: e.target.value })} placeholder="Ex: Eletricista, Pintor" className="rounded-xl" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModalPrestador(false)} className="rounded-xl">Cancelar</Button>
                <Button data-testid="prest-salvar" onClick={criarPrestador} className="rounded-xl bg-foreground text-background hover:bg-foreground/90">Cadastrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <p className="text-xs text-muted-foreground leading-relaxed border border-dashed border-border p-3 rounded-xl">
            Prestadores cadastrados aqui aparecem automaticamente na lista de autorização da portaria/segurança.
          </p>

          {prestadores.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm border border-dashed rounded-2xl">
              Nenhum prestador cadastrado.
            </div>
          ) : (
            prestadores.map((p) => (
              <Card key={p.id} data-testid={`loj-prest-${p.id}`} className="p-4 rounded-2xl border-border flex items-center justify-between">
                <div>
                  <div className="font-semibold">{p.nome}</div>
                  <div className="text-xs font-mono text-muted-foreground">
                    RG {p.rg} {p.cpf && `· CPF ${p.cpf}`}
                  </div>
                  {p.funcao && <div className="text-xs text-muted-foreground mt-1">{p.funcao}</div>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => delPrestador(p.id)} data-testid={`del-prest-${p.id}`}>
                  <Trash2 size={14} className="text-destructive" />
                </Button>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
